import { useAuth0 } from "@auth0/auth0-react";
import { ThemeToggle } from "../../components/theme";

// Public marketing landing — the pre-auth home (rendered by main.tsx's Gate
// for unauthenticated visitors). Two CTAs route into Auth0 Universal Login:
// "Entrar" = login; "Criar conta" = signup via screen_hint. After auth, Auth0
// returns to redirect_uri (origin) and the app routes the user to the
// dashboard or, if they have no workspace yet, to /onboarding (OnboardingGuard).

const FEATURES: Array<{ title: string; body: string }> = [
  {
    title: "Visão DMARC consolidada",
    body: "Pass rate de SPF e DKIM, dispositions e tendências por domínio — agregados prontos, sem planilha.",
  },
  {
    title: "Fontes e IPs enriquecidos",
    body: "rDNS, ASN e classificação de cada IP remetente, separando ESPs legítimos de spoofing.",
  },
  {
    title: "Monitoramento de DNS",
    body: "Detecta mudanças nos registros SPF/DMARC/DKIM e alerta sobre desvios e quedas de relatórios.",
  },
  {
    title: "Onboarding em minutos",
    body: "Crie a workspace, verifique o domínio por TXT e publique o rua= — comece a receber relatórios no mesmo dia.",
  },
];

export function Landing() {
  const { loginWithRedirect } = useAuth0();
  const login = () => loginWithRedirect();
  const signup = () => loginWithRedirect({ authorizationParams: { screen_hint: "signup" } });

  return (
    <div className="min-h-screen bg-slate-50 text-slate-800 dark:bg-slate-950 dark:text-slate-200">
      {/* Top bar */}
      <header className="border-b border-slate-200 dark:border-slate-800">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-6 py-4">
          <div className="flex items-center gap-2">
            <img src="/etus-logo.png" alt="Etus" className="h-8 w-8 rounded-lg shadow-sm" />
            <span className="font-semibold text-slate-900 dark:text-slate-100">Etus DMARC Monitor</span>
          </div>
          <div className="flex items-center gap-3">
            <ThemeToggle />
            <button
              onClick={login}
              className="rounded-md border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-100 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800"
            >
              Entrar
            </button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="mx-auto max-w-5xl px-6 pt-16 pb-12 text-center">
        <h1 className="mx-auto max-w-2xl text-4xl font-bold tracking-tight text-slate-900 dark:text-slate-50">
          Visibilidade total do seu DMARC, SPF e DKIM
        </h1>
        <p className="mx-auto mt-4 max-w-xl text-base text-slate-600 dark:text-slate-400">
          Receba e interprete os relatórios DMARC agregados dos seus domínios — descubra quem envia
          em seu nome, corrija autenticação e avance com segurança para <code className="text-sm">p=reject</code>.
        </p>
        <div className="mt-8 flex items-center justify-center gap-3">
          <button
            onClick={signup}
            className="rounded-md bg-slate-900 px-5 py-2.5 text-sm font-semibold text-white hover:bg-slate-800 dark:bg-white dark:text-slate-900 dark:hover:bg-slate-200"
          >
            Criar conta grátis
          </button>
          <button
            onClick={login}
            className="rounded-md border border-slate-300 px-5 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-100 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800"
          >
            Entrar
          </button>
        </div>
        <p className="mt-3 text-xs text-slate-400">Plano Free — sem cartão de crédito.</p>
      </section>

      {/* Features */}
      <section className="mx-auto max-w-5xl px-6 pb-16">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {FEATURES.map((f) => (
            <div
              key={f.title}
              className="rounded-lg border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900"
            >
              <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">{f.title}</h3>
              <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">{f.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Free plan callout */}
      <section className="mx-auto max-w-5xl px-6 pb-20">
        <div className="rounded-xl border border-slate-200 bg-white p-8 text-center dark:border-slate-800 dark:bg-slate-900">
          <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-100">Comece no plano Free</h2>
          <p className="mx-auto mt-2 max-w-md text-sm text-slate-600 dark:text-slate-400">
            1 domínio monitorado, 30 dias de histórico de relatórios e o dashboard completo. Faça o
            upgrade quando precisar de mais domínios e retenção.
          </p>
          <button
            onClick={signup}
            className="mt-5 rounded-md bg-slate-900 px-5 py-2.5 text-sm font-semibold text-white hover:bg-slate-800 dark:bg-white dark:text-slate-900 dark:hover:bg-slate-200"
          >
            Criar conta grátis
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-slate-200 dark:border-slate-800">
        <div className="mx-auto flex max-w-5xl flex-col items-center justify-between gap-2 px-6 py-6 text-xs text-slate-500 sm:flex-row">
          <span>© Etus DMARC Monitor</span>
          <div className="flex gap-4">
            <a href="/terms" className="hover:text-slate-700 dark:hover:text-slate-300">Termos</a>
            <a href="/privacy" className="hover:text-slate-700 dark:hover:text-slate-300">Privacidade</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
